import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
} from "react";
import { useHeroCampaigns } from "@/lib/siteContent";

/**
 * Hero — carrossel de campanhas fotográficas.
 *
 * A fotografia ocupa toda a área; nenhum texto, selo, CTA, número ou elemento
 * gráfico é exibido sobre a imagem. Cada campanha entrega um formato próprio
 * por breakpoint via <picture>/<source>, de modo que o navegador baixa apenas a
 * imagem adequada ao dispositivo.
 */
const AUTOPLAY_MS = 5500;
const SLIDE_TRANSITION_MS = 1200;
const DESKTOP_QUERY = "(min-width: 768px)";

export default function Hero() {
  const campaigns = useHeroCampaigns();
  const total = campaigns.length;

  const [index, setIndex] = useState(0);
  const [hoverPaused, setHoverPaused] = useState(false);
  const [userPaused, setUserPaused] = useState(false);
  const [reduced, setReduced] = useState(false);
  const [mounted, setMounted] = useState<number[]>([0]);
  const dragRef = useRef<number | null>(null);

  const active = total ? index % total : 0;

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const handler = () => setReduced(mq.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  // Monta a imagem da campanha somente quando ela entra em cena.
  useEffect(() => {
    setMounted((prev) => (prev.includes(active) ? prev : [...prev, active]));
  }, [active]);

  // Pré-carrega apenas a próxima campanha, no formato adequado ao dispositivo.
  useEffect(() => {
    if (reduced || total === 0) return;
    const next = campaigns[(active + 1) % total];
    const isDesktop = window.matchMedia(DESKTOP_QUERY).matches;
    const loader = new Image();
    loader.decoding = "async";
    loader.src = isDesktop ? next.imgDesktop : next.imgMobile;
  }, [active, campaigns, reduced, total]);

  useEffect(() => {
    if (userPaused || hoverPaused || reduced || total < 2) return undefined;
    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % total);
    }, AUTOPLAY_MS);
    return () => window.clearInterval(timer);
  }, [userPaused, hoverPaused, reduced, total]);

  // Qualquer interação manual pausa o autoplay.
  const go = (direction: number) => {
    if (total === 0) return;
    setUserPaused(true);
    setIndex((current) => (current + direction + total) % total);
  };

  const goTo = (target: number) => {
    setUserPaused(true);
    setIndex(target);
  };

  const togglePause = () => setUserPaused((current) => !current);

  const handlePointerDown = (event: PointerEvent<HTMLElement>) => {
    dragRef.current = event.clientX;
  };

  const handlePointerUp = (event: PointerEvent<HTMLElement>) => {
    if (dragRef.current === null) return;
    const delta = event.clientX - dragRef.current;
    if (Math.abs(delta) > 44) go(delta < 0 ? 1 : -1);
    dragRef.current = null;
  };

  const handleKey = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "ArrowRight") {
      event.preventDefault();
      go(1);
    }
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      go(-1);
    }
  };

  if (total === 0) return null;

  return (
    <section
      aria-roledescription="carrossel"
      aria-label="Campanhas Auréa"
      tabIndex={0}
      className="group relative -mt-16 h-[74svh] w-full cursor-grab overflow-hidden bg-foreground-950 outline-none md:-mt-20 md:h-[82svh]"
      style={{ minHeight: "420px" }}
      onKeyDown={handleKey}
      onMouseEnter={() => setHoverPaused(true)}
      onMouseLeave={() => setHoverPaused(false)}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerLeave={() => {
        dragRef.current = null;
      }}
    >
      {/* Telas de campanha — a imagem ocupa toda a área */}
      <div className="absolute inset-0">
        {campaigns.map((campaign, i) => {
          if (!mounted.includes(i)) return null;
          const isActive = i === active;
          const offset = reduced ? 0 : isActive ? 0 : i > active ? 26 : -26;
          const isFirst = i === 0;
          return (
            <div
              key={campaign.id}
              aria-hidden={!isActive}
              className={`absolute inset-0 ${reduced ? "" : "transition-[opacity,transform]"} ${
                isActive ? "opacity-100" : "pointer-events-none opacity-0"
              }`}
              style={{
                transform: `translateX(${offset}px)`,
                transitionDuration: reduced ? "0ms" : `${SLIDE_TRANSITION_MS}ms`,
                transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
              }}
            >
              <picture>
                <source media={DESKTOP_QUERY} srcSet={campaign.imgDesktop} />
                <img
                  src={campaign.imgMobile}
                  alt={campaign.alt}
                  title={`Auréa — ${campaign.name}`}
                  width={900}
                  height={1200}
                  decoding={isFirst ? "auto" : "async"}
                  fetchPriority={isFirst ? "high" : "auto"}
                  className="h-full w-full object-cover object-center"
                />
              </picture>
            </div>
          );
        })}
      </div>

      {/* Véu sutil apenas para manter o cabeçalho legível sobre a foto */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 z-10 h-20 bg-gradient-to-b from-background-50/60 to-transparent md:h-24"
      />

      {/* Setas discretas (somente desktop, aparecem ao interagir) */}
      <button
        type="button"
        onClick={() => go(-1)}
        aria-label="Campanha anterior"
        className="absolute left-3 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-background-50/70 text-foreground-900 opacity-0 backdrop-blur-sm transition-opacity duration-300 hover:bg-background-50 focus-visible:opacity-100 group-hover:opacity-100 md:left-6 md:flex"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        onClick={() => go(1)}
        aria-label="Próxima campanha"
        className="absolute right-3 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-background-50/70 text-foreground-900 opacity-0 backdrop-blur-sm transition-opacity duration-300 hover:bg-background-50 focus-visible:opacity-100 group-hover:opacity-100 md:right-6 md:flex"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      {/* Indicadores + controle de pausa — sem bloco de fundo, visíveis ao interagir */}
      <div className="absolute inset-x-0 bottom-5 z-20 flex items-center justify-center gap-4 md:bottom-7">
        <div className="flex items-center gap-2 opacity-100 mix-blend-difference transition-opacity duration-300 md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100">
          {campaigns.map((campaign, i) => (
            <button
              key={`${campaign.id}-dot`}
              type="button"
              onClick={() => goTo(i)}
              aria-label={`Ver campanha ${i + 1}`}
              aria-current={i === active}
              className="flex h-6 w-6 cursor-pointer items-center justify-center"
            >
              <span
                className={`block h-[3px] rounded-full bg-white transition-all duration-300 ${
                  i === active ? "w-6 opacity-100" : "w-3 opacity-60"
                }`}
              />
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={togglePause}
          aria-label={userPaused ? "Reproduzir campanhas" : "Pausar campanhas"}
          aria-pressed={userPaused}
          className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full text-white opacity-100 mix-blend-difference transition-opacity duration-300 focus-visible:opacity-100 md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100"
        >
          <i className={`${userPaused ? "ri-play-fill" : "ri-pause-fill"} text-base`} />
        </button>
      </div>
    </section>
  );
}